/**
 * ExtractionProgress Component
 *
 * Displays real-time progress of entity extraction with stage indicators
 */

import { Loader2, CheckCircle2, AlertCircle, FileText, Database, Sparkles } from 'lucide-react'
import type { ExtractionProgress as ExtractionProgressType } from '@/hooks/useDocumentProcessing'

interface ExtractionProgressProps {
  progress: ExtractionProgressType
  className?: string
}

export function ExtractionProgress({ progress, className = '' }: ExtractionProgressProps) {
  const { stage, entitiesFound, relationshipsFound, fromCache, error } = progress

  // Stage configuration
  const stages = [
    {
      key: 'analyzing',
      label: 'Analyzing',
      icon: Sparkles,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      key: 'saving',
      label: 'Saving',
      icon: Database,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      key: 'complete',
      label: 'Complete',
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
  ]

  const currentStageIndex = stages.findIndex((s) => s.key === stage)
  const isError = stage === 'error'

  if (stage === 'idle') {
    return null
  }

  return (
    <div className={`bg-white border border-warm-200 rounded-lg p-4 ${className}`}>
      {/* Error State */}
      {isError && (
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-red-700">Extraction Failed</div>
            <div className="text-sm text-red-600 mt-1">{error || 'Unknown error occurred'}</div>
          </div>
        </div>
      )}

      {/* Progress State */}
      {!isError && (
        <>
          {/* Stage Indicators */}
          <div className="flex items-center justify-between mb-4">
            {stages.map((stageConfig, index) => {
              const Icon = stageConfig.icon
              const isActive = stage === stageConfig.key
              const isCompleted = index < currentStageIndex
              const isUpcoming = index > currentStageIndex

              return (
                <div key={stageConfig.key} className="flex items-center flex-1">
                  {/* Stage Circle */}
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                      isActive
                        ? `${stageConfig.bgColor} ring-2 ring-offset-2 ring-${stageConfig.color.split('-')[1]}-500`
                        : isCompleted
                        ? 'bg-green-50'
                        : 'bg-warm-100'
                    }`}
                  >
                    {isActive && (
                      <Loader2 className={`h-5 w-5 ${stageConfig.color} animate-spin`} />
                    )}
                    {isCompleted && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                    {isUpcoming && <Icon className="h-5 w-5 text-warm-400" />}
                  </div>

                  {/* Stage Label */}
                  <div className="ml-3 flex-1">
                    <div
                      className={`text-sm font-medium ${
                        isActive
                          ? 'text-text-primary'
                          : isCompleted
                          ? 'text-green-600'
                          : 'text-text-secondary'
                      }`}
                    >
                      {stageConfig.label}
                    </div>
                  </div>

                  {/* Connector Line */}
                  {index < stages.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 mx-2 transition-all ${
                        isCompleted ? 'bg-green-500' : 'bg-warm-200'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>

          {/* Progress Stats */}
          <div className="flex items-center gap-4 text-sm">
            {fromCache && (
              <div className="flex items-center gap-1.5 text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                <Sparkles className="h-4 w-4" />
                <span className="font-medium">From Cache</span>
              </div>
            )}

            {entitiesFound > 0 && (
              <div className="flex items-center gap-1.5 text-text-secondary">
                <FileText className="h-4 w-4" />
                <span>
                  <span className="font-medium text-text-primary">{entitiesFound}</span> entities
                </span>
              </div>
            )}

            {relationshipsFound > 0 && (
              <div className="flex items-center gap-1.5 text-text-secondary">
                <Database className="h-4 w-4" />
                <span>
                  <span className="font-medium text-text-primary">{relationshipsFound}</span>{' '}
                  relationships
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
